import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { environment } from '@environment/environment';
import { TokenStoreService } from './token-store.service';

interface OAuthTokenResponse {
	access_token: string;
	refresh_token: string;
	expires_in: number;
	token_type: string;
}

/**
 * CN-8597 — exchanges a username + password for a hecate-issued JWT via the
 * /oauth/token password grant and persists the tokens through
 * {@link TokenStoreService}.
 *
 * Lives in one place so the main login effect and the post-registration /
 * post-reset auto-login flows share the same implementation. It uses
 * {@link HttpClient} directly (not RestService) because the token endpoint is
 * on a different host ({@link environment.AUTH_SERVER_URL}), needs a
 * form-urlencoded body with an explicit Content-Type, and must NOT carry the
 * c4 session cookie / Authorization header — none of which RestService can do.
 */
@Injectable({ providedIn: 'root' })
export class HecateAuthService {
	constructor(
		private readonly http: HttpClient,
		private readonly tokenStore: TokenStoreService
	) {}

	/**
	 * Runs the confidential-client password grant and stores the resulting
	 * access/refresh tokens. Emits once on success; errors pass through so the
	 * caller can surface "invalid credentials".
	 */
	passwordGrant(username: string, password: string): Observable<void> {
		const body = new URLSearchParams();
		body.set('grant_type', 'password');
		body.set('client_id', environment.AUTH_CLIENT_ID);
		body.set('client_secret', environment.AUTH_CLIENT_SECRET);
		body.set('username', username);
		body.set('password', password);

		const headers = new HttpHeaders({
			'Content-Type': 'application/x-www-form-urlencoded',
		});

		return this.http
			.post<OAuthTokenResponse>(`${environment.AUTH_SERVER_URL}/oauth/token`, body.toString(), {
				headers,
			})
			.pipe(
				tap((res) => this.tokenStore.setTokens(res.access_token, res.refresh_token, res.expires_in)),
				map(() => void 0)
			);
	}
}
